import {
  Box,
  Button,
  Center,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormLabel,
  Heading,
  Image,
  Radio,
  RadioGroup,
  Stack,
  Text
} from '@chakra-ui/react'
import { Chart, registerables } from 'chart.js'
import { useEffect, useState } from 'react'
import { Bar } from 'react-chartjs-2'

import Counter from '../../../components/counter/Counter'
import { Icon } from '../../../components/Icon'
import { LoadingBlock } from '../../../components/Loading'
import { StarRating } from '../../../components/StarRating'
import WysiwygContent from '../../../components/WysiwygContent'
import { formatApiDate } from '../../../helpers'
import { IAceleraMagentaAnswer } from '../../../interfaces/IAceleraMagentaAnswer'
import AceleraMagentaService from '../../../services/AceleraMagenta/AceleraMagentaService'

Chart.register(...registerables)

interface IFormField {
  id: number
  title: string
  type: string
  options: string
  groups: string
}

interface IChartData {
  name: string
  value: number
}

interface IMetrics {
  min: number
  max: number
  message: string
}

export default function AceleraMagentaViewPage({
  aceleraMagentaId: id,
  skipLastStep
}: {
  aceleraMagentaId: number
  skipLastStep: boolean
}) {
  const aceleraMagentaService = new AceleraMagentaService()
  const [form, setForm] = useState<IFormField[]>([])
  const [formTitle, setFormTitle] = useState<string>('')
  const [formAnswers, setFormAnswers] = useState<IAceleraMagentaAnswer>({})
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [avaliacaoArpe, setAvaliacaoArpe] = useState<any[]>([])
  const [chartData, setChartData] = useState<IChartData[]>([])
  const [maturityLevels, setMaturityLevels] = useState<{ maturity: string; message: string }[]>([])
  const [step, setStep] = useState<number>(0)
  const [metrics, setMetrics] = useState<IMetrics[]>([])
  const [score, setScore] = useState<number>(0)
  const [steps, setSteps] = useState<number[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [firstFiveStars, setFirstFiveStars] = useState<number>(0)

  useEffect(() => {
    window.scrollTo(0, 0)

    loadFormPage()
  }, [step])

  const getSteps = () => {
    aceleraMagentaService.getSteps(id).then((response) => {
      setSteps(response.data)
      setStep(response.data[0])
    })
  }

  const loadFormPage = async () => {
    setLoading(true)
    aceleraMagentaService
      .getResponses(id, step)
      .then((response) => {
        setFormAnswers({})
        setForm([])

        if (step === 3) {
          setFormTitle('Nível de Maturidade')
          setChartData(response.data.chart_data)
          setMaturityLevels(response.data.maturity_levels)

          return
        }

        if (step === 5) {
          setFormTitle('Delegação Eficaz')
          setMetrics(response.data.metrics)
          setScore(response.data.score)

          return
        }

        if ([6].includes(step)) {
          setScore(Number(response.data.score.replace(/0{0,}$/, '').replace(/(\.|\,)$/, '')))
          setMetrics(response.data.metrics)

          setFirstFiveStars(
            response.data.answers.find((answer: { answer: string }) => answer.answer == '5')?.question_id
          )
        }

        if ([9].includes(step)) {
          setFormTitle(step === 11 ? 'Avaliação ARPE Colaborador' : 'Avaliação ARPE Gestor')

          setAvaliacaoArpe(JSON.parse(response.data.score))

          setFirstFiveStars(
            response.data.answers.find((answer: { answer: string }) => answer.answer == '5')?.question_id
          )
        }

        const answers: IAceleraMagentaAnswer = {}
        response.data.answers.forEach((answer: { question_id: number; answer: string }) => {
          answers[answer.question_id] = answer.answer
        })
        setFormAnswers(answers)

        setFormTitle(response.data.form.name)
        setForm(response.data.questions)
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    getSteps()
  }, [])

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
  }

  return (
    <>
      <Center>
        <Image src="/img/acelera-magenta.png" alt="Acelera Magenta" w="65%" marginBottom="10" />
      </Center>
      {loading && <LoadingBlock size="page" />}
      {!loading && (
        <>
          <Heading as="h2" mb="4">
            {formTitle}
          </Heading>
          <form onSubmit={handleSubmit}>
            {step === 6 && (
              <Box marginBottom="5">
                <Box borderWidth="1px" p="5" borderRadius="lg" w="100%">
                  <Heading as="h3" mb="4">
                    Pontuação
                  </Heading>
                  <Box
                    borderWidth="1px"
                    p="5"
                    borderRadius="lg"
                    w="100%"
                    marginBottom="3"
                    backgroundColor="magenta.500"
                    color="#FFFFFF"
                  >
                    <Text fontSize="2xl" fontWeight="bold">
                      <Center>{score?.toString()?.replace('.', ',')} estrelas!</Center>
                    </Text>
                  </Box>

                  {metrics.map(
                    (metric) =>
                      score >= metric.min &&
                      score <= metric.max && (
                        <Box
                          key={metric.min}
                          borderWidth="1px"
                          p="5"
                          borderRadius="lg"
                          w="100%"
                          marginBottom="3"
                          backgroundColor={
                            score >= metric.min && score <= metric.max ? 'rgba(250, 0, 105)' : 'rgba(51, 51, 51)'
                          }
                          color="#FFFFFF"
                        >
                          <WysiwygContent content={metric.message.replaceAll('\n', '<br>')} />
                        </Box>
                      )
                  )}
                </Box>
              </Box>
            )}

            {step === 9 && (
              <>
                Notas ARPE
                {Object.keys(avaliacaoArpe).map((key: string) => {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  const value: number = avaliacaoArpe[key as any]
                  return (
                    <>
                      <p>
                        <b>{key}</b> - {Number(value).toFixed(2)} estrelas
                      </p>
                    </>
                  )
                })}
                <>
                  <Box marginBottom="5" marginTop="5">
                    <Box borderWidth="1px" p="5" borderRadius="lg" w="100%">
                      <Heading as="h3" mb="4">
                        <Center>Nota final ARPE</Center>
                      </Heading>
                      <Box
                        borderWidth="1px"
                        p="5"
                        borderRadius="lg"
                        w="100%"
                        marginBottom="3"
                        backgroundColor="magenta.500"
                        color="#FFFFFF"
                      >
                        <Text fontSize="2xl" fontWeight="bold">
                          <Center>
                            {(Object.values(avaliacaoArpe).reduce((a: number, b: number) => a + b, 0) / 4).toFixed(2)}{' '}
                            estrelas!
                          </Center>
                        </Text>
                      </Box>
                    </Box>
                  </Box>
                </>
              </>
            )}

            {form.map((field) => {
              return (
                <>
                  {field.id === firstFiveStars && (
                    <>
                      <hr />
                      <Text mb="3" mt="3">
                        <strong>Parabéns pelas 5 estrelas nos itens abaixo!</strong>
                      </Text>
                    </>
                  )}
                  <FormControl key={field.id} mb="4">
                    <FormLabel>{field.title}</FormLabel>
                    {field.type === 'textarea' &&
                      formAnswers[field.id].split('\n').map((line, index) => <Text key={index}>{line}</Text>)}
                    {field.type === 'text' &&
                      formAnswers[field.id].split('\n').map((line, index) => <Text key={index}>{line}</Text>)}
                    {field.type === 'date' && formatApiDate(formAnswers[field.id])}
                    {field.type === 'options' && (
                      <RadioGroup value={formAnswers[field.id]}>
                        <Stack direction="column" spacing={3}>
                          {(JSON.parse(field.options) as string[]).map((option, index) => (
                            <Radio value={(index + 1).toString()} key={index} isDisabled>
                              {step !== 2 && step !== 4 && option}
                              {step === 2 && (
                                <>
                                  {JSON.parse(field.groups)[index] == 4 && (
                                    <Text color="green" fontWeight="semibold">
                                      {option}
                                    </Text>
                                  )}
                                  {JSON.parse(field.groups)[index] != 4 &&
                                    ((formAnswers[field.id] == (index + 1).toString() &&
                                      JSON.parse(field.groups)[parseInt(formAnswers[field.id]) - 1] != 4 && (
                                        <Text color="tomato" fontWeight="semibold">
                                          {option}
                                        </Text>
                                      )) ||
                                      option)}
                                </>
                              )}
                              {step === 4 &&
                                formAnswers[field.id] == (index + 1).toString() &&
                                ((JSON.parse(field.groups)[parseInt(formAnswers[field.id]) - 1] == 1 && (
                                  <Text color="green" fontWeight="semibold">
                                    {option}
                                  </Text>
                                )) ||
                                  (JSON.parse(field.groups)[parseInt(formAnswers[field.id]) - 1] != 1 && (
                                    <Text color="tomato" fontWeight="semibold">
                                      {option}
                                    </Text>
                                  )))}
                              {step === 4 && formAnswers[field.id] != (index + 1).toString() && option}
                            </Radio>
                          ))}
                        </Stack>
                      </RadioGroup>
                    )}
                    {field.type === 'checkbox' && (
                      <CheckboxGroup value={JSON.parse(formAnswers[field.id])}>
                        {(JSON.parse(field.options) as string[]).map((option, index) => (
                          <Checkbox value={(index + 1).toString()} key={index} marginLeft="2">
                            {option}
                          </Checkbox>
                        ))}
                      </CheckboxGroup>
                    )}
                    {field.type === 'stars' && <StarRating value={parseInt(formAnswers[field.id] ?? '0')} />}
                  </FormControl>
                </>
              )
            })}

            {step === 3 && (
              <>
                <Bar
                  data={{
                    labels: ['Bebê', 'Criança', 'Adolescente', 'Adulto'],
                    datasets: [
                      {
                        label: 'Nível de Maturidade',
                        data: chartData.map((data) => data.value),
                        backgroundColor: chartData.map((data) =>
                          isBiggest(
                            data.value,
                            chartData.map((d) => d.value)
                          )
                            ? 'rgba(250, 0, 105, 0.8)'
                            : 'rgba(51, 51, 51, 0.8)'
                        )
                      }
                    ]
                  }}
                />
                <br />
                {maturityLevels.map((level, key) => (
                  <Box
                    key={level.maturity}
                    borderWidth="1px"
                    p="5"
                    borderRadius="lg"
                    w="100%"
                    marginBottom="3"
                    backgroundColor={
                      isBiggest(
                        chartData[key].value,
                        chartData.map((d) => d.value)
                      )
                        ? 'rgba(250, 0, 105)'
                        : 'rgba(51, 51, 51)'
                    }
                    color="#FFFFFF"
                  >
                    <Heading as="h3" mb="4">
                      {level.maturity}
                    </Heading>
                    <WysiwygContent content={level.message.replaceAll('\n', '<br>')} />
                  </Box>
                ))}
                <br />
              </>
            )}

            {step === 5 && (
              <Box marginBottom="5">
                <Box borderWidth="1px" p="5" borderRadius="lg" w="100%">
                  <Heading as="h3" mb="4">
                    Pontuação
                  </Heading>
                  <Box
                    borderWidth="1px"
                    p="5"
                    borderRadius="lg"
                    w="100%"
                    marginBottom="3"
                    backgroundColor="magenta.500"
                    color="#FFFFFF"
                  >
                    <Text fontSize="2xl" fontWeight="bold">
                      <Center>
                        <Counter limit={score} />
                        &nbsp;pontos!
                      </Center>
                    </Text>
                  </Box>

                  {metrics.map((metric) => (
                    <Box
                      key={metric.min}
                      borderWidth="1px"
                      p="5"
                      borderRadius="lg"
                      w="100%"
                      marginBottom="3"
                      backgroundColor={
                        score >= metric.min && score <= metric.max ? 'rgba(250, 0, 105)' : 'rgba(51, 51, 51)'
                      }
                      color="#FFFFFF"
                    >
                      <WysiwygContent
                        content={`De ${metric.min} a ${metric.max} respostas corretas - ${metric.message.replaceAll(
                          '\n',
                          '<br>'
                        )}`}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            <Box display={['block', 'block', 'none']}>
              <br />
              <Button
                colorScheme="magenta"
                type="button"
                onClick={() => setStep(steps[steps.indexOf(step) - 1])}
                float="left"
                disabled={step <= steps[0]}
              >
                <Icon name="lni lni-arrow-left" />
                &nbsp;Passo anterior
              </Button>
              <Button
                colorScheme="magenta"
                type="button"
                onClick={() => setStep(steps[steps.indexOf(step) + 1])}
                float="right"
                disabled={skipLastStep ? step >= steps[steps.length - 2] : step >= steps[steps.length - 1]}
              >
                Próximo passo &nbsp;
                <Icon name="lni lni-arrow-right" />
              </Button>
            </Box>

            <Box display={['none', 'none', 'block']}>
              <Button
                colorScheme="blackAlpha"
                opacity="90%"
                type="button"
                onClick={() => setStep(steps[steps.indexOf(step) - 1])}
                position={'fixed'}
                left={'5rem'}
                bottom={'50%'}
                fontSize={'xxx-large'}
                padding={'30px 5px'}
                textAlign={'center'}
                disabled={step <= steps[0]}
              >
                <Icon name="lni lni-chevron-left" />
              </Button>
              <Button
                colorScheme="blackAlpha"
                opacity="90%"
                type="button"
                onClick={() => setStep(steps[steps.indexOf(step) + 1])}
                position={'fixed'}
                right={'6rem'}
                bottom={'50%'}
                fontSize={'xxx-large'}
                padding={'30px 5px'}
                textAlign={'center'}
                disabled={steps.indexOf(step) >= steps.length - (skipLastStep ? 2 : 1)}
              >
                <Icon name="lni lni-chevron-right" />
              </Button>
            </Box>
          </form>
        </>
      )}
    </>
  )
}

const isBiggest = (number: number, array: number[]) => {
  return number == Math.max(...array)
}
